import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { fetchMe } from '../lib/auth';
import { ProfileCard } from '../components/profile-card';
import { RewardsBox } from '../components/rewards-box';
import { KanbanColumn } from '../components/kanban/column';
import { KanbanCard } from '../components/kanban/card';
import { TemplateModal } from '../components/kanban/template-modal';
import { AddAchievementModal } from '../components/kanban/add-achievement-modal';

export const Route = createFileRoute('/')({ component: HomePage });

interface TaskInstance {
  id: string;
  templateId: string | null;
  title: string;
  energy: number;
  completedAt: string | null;
  skipped: boolean;
  xpAwarded: number | null;
  kind: 'habit' | 'task';
}

interface Template {
  id: string;
  title: string;
  energy: number;
  scheduleRule: string;
  active: boolean;
}

interface Achievement {
  id: string;
  name: string;
  type: 'due_date' | 'recurring';
  status: 'active' | 'completed' | 'failed' | 'archived';
  currentLevel: number;
  occurrenceCount: number;
  dueAt: string | null;
}

type AddMode = null | 'habit' | 'task' | 'ach';

function HomePage() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const today = useQuery({ queryKey: ['today'], queryFn: () => api<TaskInstance[]>('/api/tasks/today') });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/tasks/templates') });
  const achievements = useQuery({
    queryKey: ['achievements'],
    queryFn: () => api<Achievement[]>('/api/achievements'),
  });
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [editing, setEditing] = useState<{ kind: 'habit' | 'task'; tpl: Template } | null>(null);

  const complete = useMutation({
    mutationFn: (id: string) => api(`/api/tasks/instances/${id}/complete`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['today'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const skip = useMutation({
    mutationFn: (id: string) => api(`/api/tasks/instances/${id}/skip`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['today'] }),
  });
  const deleteTpl = useMutation({
    mutationFn: (id: string) => api(`/api/tasks/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['today'] });
    },
  });

  const tasks = today.data ?? [];
  const tplList = templates.data ?? [];
  const tplById = new Map(tplList.map((t) => [t.id, t]));
  const habits = tasks.filter((t) => t.kind === 'habit');
  const dailyTasks = tasks.filter((t) => t.kind === 'task');
  const activeAch = (achievements.data ?? []).filter((a) => a.status === 'active');

  const todayEnergy = tasks.filter((t) => t.completedAt).reduce((s, t) => s + t.energy, 0);

  function cardPropsFor(t: TaskInstance): {
    schedule: string | undefined;
    onEdit?: () => void;
    onDelete?: () => void;
  } {
    const tpl = t.templateId ? tplById.get(t.templateId) : undefined;
    return {
      schedule: tpl ? scheduleLabel(tpl.scheduleRule) : undefined,
      ...(tpl ? {
        onEdit: () => setEditing({ kind: t.kind, tpl }),
        onDelete: () => confirm(`"${tpl.title}" silinsin mi?`) && deleteTpl.mutate(tpl.id),
      } : {}),
    };
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-6 py-6">
      <section className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1.2fr]">
        <ProfileCard
          displayName={me.data?.displayName ?? 'Kahraman'}
          avatarEmoji={me.data?.avatarEmoji ?? '🧙'}
          level={me.data?.level ?? 1}
          xpIntoLevel={me.data?.xpIntoLevel ?? 0}
          xpForNextLevel={me.data?.xpForNextLevel ?? 100}
          todayEnergy={todayEnergy}
        />
        <RewardsBox />
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KanbanColumn title="Alışkanlıklar" accent="habit" icon="🌱"
                      count={habits.length} onAdd={() => setAddMode('habit')}>
          {habits.length === 0 && <EmptyCol />}
          {habits.map((t) => {
            const p = cardPropsFor(t);
            return (
              <KanbanCard key={t.id} accent="habit"
                title={t.title}
                energy={t.energy}
                schedule={p.schedule}
                xp={t.xpAwarded ?? undefined}
                done={!!t.completedAt}
                disabled={!!t.completedAt || t.skipped}
                onToggle={() => complete.mutate(t.id)}
                onSkip={() => skip.mutate(t.id)}
                onEdit={p.onEdit}
                onDelete={p.onDelete} />
            );
          })}
        </KanbanColumn>

        <KanbanColumn title="Günlük Görevler" accent="task" icon="⚡"
                      count={dailyTasks.length} onAdd={() => setAddMode('task')}>
          {dailyTasks.length === 0 && <EmptyCol />}
          {dailyTasks.map((t) => {
            const p = cardPropsFor(t);
            return (
              <KanbanCard key={t.id} accent="task"
                title={t.title}
                energy={t.energy}
                schedule={p.schedule}
                xp={t.xpAwarded ?? undefined}
                done={!!t.completedAt}
                disabled={!!t.completedAt || t.skipped}
                onToggle={() => complete.mutate(t.id)}
                onSkip={() => skip.mutate(t.id)}
                onEdit={p.onEdit}
                onDelete={p.onDelete} />
            );
          })}
        </KanbanColumn>

        <KanbanColumn title="Achievements" accent="ach" icon="🏆"
                      count={activeAch.length} onAdd={() => setAddMode('ach')}>
          {activeAch.length === 0 && <EmptyCol />}
          {activeAch.map((a) => (
            <KanbanCard key={a.id} accent="ach"
              title={a.name}
              schedule={a.type === 'recurring'
                ? `Lv ${a.currentLevel} · ${a.occurrenceCount} occ`
                : (a.dueAt ? new Date(a.dueAt).toLocaleDateString('tr-TR') : undefined)}
              done={false}
              onToggle={() => { /* phase ileri: complete */ }} />
          ))}
        </KanbanColumn>
      </section>

      {(addMode === 'habit' || addMode === 'task') && (
        <TemplateModal kind={addMode} onClose={() => setAddMode(null)} />
      )}
      {addMode === 'ach' && <AddAchievementModal onClose={() => setAddMode(null)} />}
      {editing && <TemplateModal kind={editing.kind} existing={editing.tpl}
                                 onClose={() => setEditing(null)} />}
    </div>
  );
}

function scheduleLabel(rule: string): string {
  const s = rule.trim().toLowerCase();
  if (s === 'once') return 'tek seferlik';
  const d = s.match(/^daily\s+(\d{1,2}):(\d{2})$/);
  if (d) return `her gün ${d[1]!.padStart(2, '0')}:${d[2]}`;
  const w = s.match(/^weekly\s+([a-z,]+)\s+(\d{1,2}):(\d{2})$/);
  if (w) {
    const days = w[1]!.split(',').map((x) => DAY_TR[x] ?? x).join(', ');
    return `${days} ${w[2]!.padStart(2, '0')}:${w[3]}`;
  }
  return rule;
}
const DAY_TR: Record<string, string> = {
  mon: 'Pzt', tue: 'Sal', wed: 'Çar', thu: 'Per', fri: 'Cum', sat: 'Cmt', sun: 'Paz',
};

function EmptyCol() {
  return (
    <li className="rounded-md border border-dashed border-border-soft/60 p-4 text-center text-xs text-neutral-500">
      Henüz yok
    </li>
  );
}
